/**
 * Reading a picked file for import, on platforms that cannot be trusted to keep one around.
 *
 * The `File` handed over by `<input type="file">` is a lazy handle to something on disk, not the
 * bytes themselves. On iPadOS a file picked through the Files app is exposed to WebKit as a
 * security-scoped temporary copy, and iPadOS 18.7 reaps that copy far more eagerly than earlier
 * versions did. Serialising the stale handle into a `FormData` does not throw — WebKit sends the
 * multipart part with its headers and an empty body, and the backend answers "No file uploaded".
 *
 * So the bytes are read once, at the moment of selection, and everything downstream — the type
 * check, the canvas preview, the upload — is served from that in-memory copy. A `File` backed by
 * an `ArrayBuffer` has no disk handle left to go stale.
 *
 * The same platform also hands us files with no extension and an empty `type`, so the type is
 * sniffed from the leading magic bytes and treated as authoritative. `file.name` and `file.type`
 * are never used to decide anything.
 *
 * SECURITY — the magic-byte sniff is the import gate, and it is deliberately the *only* gate.
 * Its job is to stop content masquerading as something it is not: an executable renamed to
 * `.pdf` never matches a pattern in `MIMES_LIST`, so it is refused. The previous code checked the
 * sniff *and* an allowlist of `file.type`, but `file.type` is derived from the extension, which is
 * the one thing an attacker controls — so the allowlist was being applied to untrusted input while
 * the sniff merely confirmed the two agreed. Applying the allowlist to the sniffed value instead
 * removes that indirection: the decision now rests entirely on what is inside the file.
 *
 * The type handed to the backend comes from the sniff too (`buildUploadFile`), so multer's own
 * `fileFilter` is fed a content-derived value rather than an extension-derived one.
 *
 * Reading bytes is inert — `FileReader` copies, it does not parse, decode or execute — so pulling
 * an unwanted file into an `ArrayBuffer` before rejecting it carries no execution risk. The only
 * cost is the memory, which `MAX_IMPORT_FILE_SIZE` bounds before a single byte is read.
 */

import { Constants } from '../constant/constants';

export const IMPORT_MIME = {
  PNG: 'image/png',
  JPEG: 'image/jpeg',
  PDF: 'application/pdf',
  KIWI: 'application/kiwi',
};

const EXTENSION_BY_MIME: Record<string, string> = {
  [IMPORT_MIME.PNG]: 'png',
  [IMPORT_MIME.JPEG]: 'jpg',
  [IMPORT_MIME.PDF]: 'pdf',
  [IMPORT_MIME.KIWI]: 'kiwi',
};

/** btoa() takes an argument, not a whole file — a multi-MB spread blows the call stack. */
const BASE64_CHUNK_SIZE = 0x8000;

/**
 * Largest file we will pull into memory, matching multer's `limits.fileSize` on the backend.
 *
 * Checked from `file.size`, which costs no read, so an oversized file is refused before it can be
 * buffered. Anything above this is rejected server-side regardless, so failing fast here changes
 * nothing a user could previously do — it just stops the tab paying for it first.
 */
export const MAX_IMPORT_FILE_SIZE = 100 * 1024 * 1024;

/**
 * A file that read back as zero bytes.
 *
 * Distinguished from an ordinary read failure because it is the signature of the iPadOS problem
 * above, and the advice that helps ("save it to Files first") is specific to it.
 */
export class EmptyFileError extends Error {
  constructor() {
    super('The selected file read back as empty.');
    this.name = 'EmptyFileError';
  }
}

/** A file past `MAX_IMPORT_FILE_SIZE`, refused before being read. */
export class FileTooLargeError extends Error {
  constructor() {
    super('The selected file is too large to import.');
    this.name = 'FileTooLargeError';
  }
}

/**
 * Reads a picked file into memory in one pass.
 *
 * One read, not two: the previous code sniffed the first four bytes and then read the file again
 * for its contents, which on iPadOS is two chances for the handle to have gone stale. The sniff
 * now runs against the buffer this returns.
 */
export const readFileBytes = (file: Blob): Promise<ArrayBuffer> =>
  new Promise<ArrayBuffer>((resolve, reject) => {
    // Both checks read metadata only, so an unreasonable file never reaches the buffer. A size of
    // 0 is already the stale-handle case, so there is nothing to gain by reading it.
    if (file.size === 0) {
      reject(new EmptyFileError());
      return;
    }
    if (file.size > MAX_IMPORT_FILE_SIZE) {
      reject(new FileTooLargeError());
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const bytes = reader.result as ArrayBuffer;
      // An empty read is the failure we are actually here for, and it arrives as a *successful*
      // load with nothing in it — never as an error.
      if (!bytes || bytes.byteLength === 0) {
        reject(new EmptyFileError());
        return;
      }
      resolve(bytes);
    };
    reader.onerror = () => reject(reader.error ?? new Error('The selected file could not be read.'));
    reader.onabort = () => reject(new Error('Reading the selected file was interrupted.'));

    try {
      reader.readAsArrayBuffer(file);
    } catch (err) {
      reject(err);
    }
  });

/**
 * Identifies a file by its leading bytes, returning null when nothing matches.
 *
 * This is the only type check that means anything on iPadOS, where a file emailed to the device
 * and downloaded arrives with neither an extension nor a `type`.
 */
export const sniffMimeType = (bytes: ArrayBuffer): string | null => {
  const header = new Uint8Array(bytes.slice(0, 4));

  const matches = (mime: { pattern: number[]; mask: number[] }) => mime.mask.every((mask, i) => (header[i] & mask) - mime.pattern[i] === 0);

  return Constants.MIMES_LIST.find(mime => matches(mime))?.mime ?? null;
};

export const isSupportedImportMime = (mime: string | null): boolean => !!mime && mime in EXTENSION_BY_MIME;

/**
 * Gives a filename the extension its contents call for.
 *
 * Not cosmetic: the stored S3 key is what `FileManagerService.isPdf()` later splits on `.` to
 * decide whether to re-open something as a PDF, so a file imported without an extension would
 * open once and then fail to re-open.
 */
export const normalizeFileName = (fileName: string, mime: string): string => {
  const extension = EXTENSION_BY_MIME[mime];
  const trimmed = (fileName || '').trim();
  const base = trimmed.replace(/\.[a-zA-Z0-9]+$/, '') || 'import';

  return extension ? `${base}.${extension}` : base;
};

/** An upload built from bytes we hold, with a name that matches what is actually inside it. */
export const buildUploadFile = (bytes: ArrayBuffer, fileName: string, mime: string): File =>
  new File([bytes], normalizeFileName(fileName, mime), { type: mime });

/** Data URL for the canvas, which wants an image source rather than a buffer. */
export const bytesToDataUrl = (bytes: ArrayBuffer, mime: string): string => {
  const view = new Uint8Array(bytes);
  let binary = '';

  for (let offset = 0; offset < view.length; offset += BASE64_CHUNK_SIZE) {
    binary += String.fromCharCode(...view.subarray(offset, offset + BASE64_CHUNK_SIZE));
  }

  return `data:${mime};base64,${btoa(binary)}`;
};

/** Text for the `.kiwi` path, which is JSON. */
export const bytesToText = (bytes: ArrayBuffer): string => new TextDecoder().decode(bytes);

/**
 * Rebuilds the image or PDF embedded in a `.kiwi` export.
 *
 * Returns null for anything the backend would refuse, so callers can report a bad file rather
 * than upload something that is certain to be rejected.
 */
export const base64ToFile = (base64: string, fileName: string): File | null => {
  const commaIndex = base64.indexOf(',');
  if (commaIndex === -1) {
    console.error('[file-import] Invalid base64 payload in .kiwi file');
    return null;
  }

  const mime = base64.substring(0, commaIndex).match(/:(.*?);/)?.[1] || '';
  if (![IMPORT_MIME.PDF, IMPORT_MIME.JPEG, IMPORT_MIME.PNG].includes(mime)) {
    console.error('[file-import] Unsupported embedded type in .kiwi file:', mime);
    return null;
  }

  const binary = atob(base64.substring(commaIndex + 1));
  let n = binary.length;
  const bytes = new Uint8Array(n);
  while (n--) {
    bytes[n] = binary.charCodeAt(n);
  }

  return new File([bytes], normalizeFileName(fileName, mime), { type: mime });
};
